/**
 * Composable canvas rendering functions for the 2D warehouse layout.
 * Each function draws a single layer, making the rendering pipeline testable and readable.
 */

import {
  RackPosition,
  RackDimensions,
  WarehouseDimensions,
  WarehouseObject,
  EditorState,
  PreviewRackPosition,
  HeatmapData,
  LocationActivity,
  ACTIVITY_COLORS,
  ActivityType,
  RubberBandState,
} from '../types/warehouseLayout';
import { getRotatedDimensions } from './positionCalculator';
import { PlacedBinRenderInfo, OBJECT_TYPE_COLORS } from './layoutApiMapping';
import { insightColor, INSIGHT_UNKNOWN_COLOR } from './insightUtils';
import type { ServerPendingMove } from '../../../../../services/warehouseLayoutService';

export interface RenderContext {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  scale: number;
  offsetX: number;
  offsetY: number;
}

export function computeRenderContext(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  dimensions: WarehouseDimensions,
  zoom: number,
  panOffset: { x: number; y: number }
): RenderContext {
  const scale = Math.min(
    (canvasWidth - 40) / dimensions.length,
    (canvasHeight - 40) / dimensions.width
  ) * zoom;

  const offsetX = (canvasWidth - dimensions.length * scale) / 2 + panOffset.x;
  const offsetY = (canvasHeight - dimensions.width * scale) / 2 + panOffset.y;

  return { ctx, width: canvasWidth, height: canvasHeight, scale, offsetX, offsetY };
}

export function drawBackground(rc: RenderContext) {
  rc.ctx.fillStyle = '#1f2937';
  rc.ctx.fillRect(0, 0, rc.width, rc.height);
}

export function drawFloor(rc: RenderContext, dimensions: WarehouseDimensions) {
  rc.ctx.fillStyle = '#374151';
  rc.ctx.fillRect(rc.offsetX, rc.offsetY, dimensions.length * rc.scale, dimensions.width * rc.scale);
}

/**
 * Plantegning-underlag: strekkes over hele gulvflaten med dempet opasitet,
 * slik at reoler kan plasseres «oppå tegningen». Tegnes etter gulv, før grid.
 */
export function drawBackgroundImage(
  rc: RenderContext,
  image: HTMLImageElement,
  dimensions: WarehouseDimensions
) {
  rc.ctx.save();
  rc.ctx.globalAlpha = 0.45;
  rc.ctx.drawImage(
    image,
    rc.offsetX,
    rc.offsetY,
    dimensions.length * rc.scale,
    dimensions.width * rc.scale
  );
  rc.ctx.restore();
}

export function drawGrid(rc: RenderContext, dimensions: WarehouseDimensions, gridUnit: number) {
  rc.ctx.strokeStyle = '#4b5563';
  rc.ctx.lineWidth = 0.5;
  rc.ctx.beginPath();

  for (let x = 0; x <= dimensions.length; x += gridUnit) {
    const screenX = rc.offsetX + x * rc.scale;
    rc.ctx.moveTo(screenX, rc.offsetY);
    rc.ctx.lineTo(screenX, rc.offsetY + dimensions.width * rc.scale);
  }

  for (let y = 0; y <= dimensions.width; y += gridUnit) {
    const screenY = rc.offsetY + y * rc.scale;
    rc.ctx.moveTo(rc.offsetX, screenY);
    rc.ctx.lineTo(rc.offsetX + dimensions.length * rc.scale, screenY);
  }

  rc.ctx.stroke();
}

export function drawDimensionLabels(rc: RenderContext, dimensions: WarehouseDimensions) {
  rc.ctx.fillStyle = '#9ca3af';
  rc.ctx.font = '12px sans-serif';
  rc.ctx.textAlign = 'center';
  rc.ctx.fillText(`${dimensions.length}m`, rc.offsetX + (dimensions.length * rc.scale) / 2, rc.offsetY - 8);
  rc.ctx.save();
  rc.ctx.translate(rc.offsetX - 8, rc.offsetY + (dimensions.width * rc.scale) / 2);
  rc.ctx.rotate(-Math.PI / 2);
  rc.ctx.fillText(`${dimensions.width}m`, 0, 0);
  rc.ctx.restore();
}

/**
 * Faste objekter (søyler, porter, stasjoner, sonemarkører) — tegnes under reolene.
 */
export function drawObjects(
  rc: RenderContext,
  objects: WarehouseObject[],
  showLabels: boolean,
  /** Valgt objekt (gul ramme — samme stil som bin-/reol-seleksjon) */
  selectedObjectId?: string | null
) {
  objects.forEach(obj => {
    const color = obj.color || OBJECT_TYPE_COLORS[obj.type] || '#6b7280';
    const rotated = (obj.rotation === 90 || obj.rotation === 270)
      ? { width: obj.depth, depth: obj.width }
      : { width: obj.width, depth: obj.depth };

    const screenX = rc.offsetX + obj.x * rc.scale;
    const screenY = rc.offsetY + obj.y * rc.scale;
    const screenWidth = rotated.width * rc.scale;
    const screenDepth = rotated.depth * rc.scale;

    rc.ctx.fillStyle = color + '66'; // ~40 % alpha
    rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.strokeStyle = color;
    rc.ctx.lineWidth = 1.5;
    rc.ctx.strokeRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);

    // Gul markering av valgt objekt (samme stil som bin-seleksjon)
    if (selectedObjectId && obj.id === selectedObjectId) {
      rc.ctx.strokeStyle = '#fbbf24';
      rc.ctx.lineWidth = 3;
      rc.ctx.strokeRect(screenX - screenWidth / 2 - 2, screenY - screenDepth / 2 - 2, screenWidth + 4, screenDepth + 4);
    }

    if (showLabels && obj.name && screenWidth > 30) {
      rc.ctx.fillStyle = '#e5e7eb';
      rc.ctx.font = '10px sans-serif';
      rc.ctx.textAlign = 'center';
      rc.ctx.textBaseline = 'middle';
      rc.ctx.fillText(obj.name, screenX, screenY);
    }
  });
}

/**
 * Enkeltlokasjoner (gulv/vegg/heng/skap/dock) — tegnes over gulvet, under reolene.
 */
export function drawBinPositions(
  rc: RenderContext,
  bins: PlacedBinRenderInfo[],
  showLabels: boolean,
  selectedBinCode?: string | null,
  /** Innsikt-overlay: binCode → 0–1 (null/mangler = ukjent → grå). Udefinert = overlay av. */
  valueLookup?: Map<string, number | null> | null,
  /** Flytte-modus: binCodes som er gyldige mål — grønn heltrukken ramme + svak grønn fylling */
  moveCandidateBins?: Set<string> | null
) {
  bins.forEach(bin => {
    const rotated = (bin.rotation === 90 || bin.rotation === 270)
      ? { width: bin.depthM, depth: bin.widthM }
      : { width: bin.widthM, depth: bin.depthM };

    const screenX = rc.offsetX + bin.x * rc.scale;
    const screenY = rc.offsetY + bin.y * rc.scale;
    const screenWidth = rotated.width * rc.scale;
    const screenDepth = rotated.depth * rc.scale;

    // Innsikt-farge overstyrer bin.color når overlay er aktivt
    let binColor = bin.color;
    if (valueLookup) {
      const value = valueLookup.get(bin.binCode);
      binColor = value !== null && value !== undefined ? insightColor(value) : INSIGHT_UNKNOWN_COLOR;
    }

    rc.ctx.fillStyle = binColor + '4D'; // ~30 % alpha
    rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.strokeStyle = binColor;
    rc.ctx.lineWidth = 1.5;
    rc.ctx.setLineDash([4, 3]); // stiplet = enkeltlokasjon, ikke reol
    rc.ctx.strokeRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.setLineDash([]);

    // Flytte-modus: kandidat-mål lyser grønt (heltrukken ramme + svak fylling)
    if (moveCandidateBins?.has(bin.binCode)) {
      rc.ctx.fillStyle = 'rgba(34, 197, 94, 0.18)';
      rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
      rc.ctx.strokeStyle = '#22c55e';
      rc.ctx.lineWidth = 2.5;
      rc.ctx.strokeRect(screenX - screenWidth / 2 - 1, screenY - screenDepth / 2 - 1, screenWidth + 2, screenDepth + 2);
    }

    // Gul markering av valgt enkeltlokasjon (samme stil som reol-seleksjon)
    if (selectedBinCode && bin.binCode === selectedBinCode) {
      rc.ctx.strokeStyle = '#fbbf24';
      rc.ctx.lineWidth = 3;
      rc.ctx.strokeRect(screenX - screenWidth / 2 - 2, screenY - screenDepth / 2 - 2, screenWidth + 4, screenDepth + 4);
    }

    if (showLabels && screenWidth > 24) {
      rc.ctx.fillStyle = '#f3f4f6';
      rc.ctx.font = 'bold 10px sans-serif';
      rc.ctx.textAlign = 'center';
      rc.ctx.textBaseline = 'middle';
      rc.ctx.fillText(bin.binCode, screenX, screenY);
    }
  });
}

/**
 * To-punkts skala-kalibrering: markører, målelinje («gummistrikk» mot hover
 * inntil punkt B er satt) og live avstand i meter langs linjen.
 */
export function drawCalibration(
  rc: RenderContext,
  pointA: { x: number; y: number } | null,
  pointB: { x: number; y: number } | null,
  hoverPoint: { x: number; y: number } | null
) {
  const toScreen = (p: { x: number; y: number }) => ({
    x: rc.offsetX + p.x * rc.scale,
    y: rc.offsetY + p.y * rc.scale,
  });

  const drawMarker = (p: { x: number; y: number }, label: string) => {
    const s = toScreen(p);
    rc.ctx.beginPath();
    rc.ctx.arc(s.x, s.y, 6, 0, Math.PI * 2);
    rc.ctx.fillStyle = 'rgba(251, 191, 36, 0.25)';
    rc.ctx.fill();
    rc.ctx.strokeStyle = '#fbbf24';
    rc.ctx.lineWidth = 2;
    rc.ctx.stroke();
    // Kryss i senter for presisjon
    rc.ctx.beginPath();
    rc.ctx.moveTo(s.x - 9, s.y); rc.ctx.lineTo(s.x + 9, s.y);
    rc.ctx.moveTo(s.x, s.y - 9); rc.ctx.lineTo(s.x, s.y + 9);
    rc.ctx.lineWidth = 1;
    rc.ctx.stroke();
    rc.ctx.fillStyle = '#fbbf24';
    rc.ctx.font = 'bold 11px sans-serif';
    rc.ctx.textAlign = 'center';
    rc.ctx.textBaseline = 'bottom';
    rc.ctx.fillText(label, s.x, s.y - 12);
  };

  if (!pointA) return;
  drawMarker(pointA, 'A');

  const end = pointB ?? hoverPoint;
  if (!end) return;

  const sA = toScreen(pointA);
  const sEnd = toScreen(end);
  rc.ctx.beginPath();
  rc.ctx.moveTo(sA.x, sA.y);
  rc.ctx.lineTo(sEnd.x, sEnd.y);
  rc.ctx.strokeStyle = '#fbbf24';
  rc.ctx.lineWidth = 2;
  rc.ctx.setLineDash([6, 4]);
  rc.ctx.stroke();
  rc.ctx.setLineDash([]);

  if (pointB) drawMarker(pointB, 'B');

  // Avstand i «dagens meter» langs linjen (før kalibrering)
  const dist = Math.hypot(end.x - pointA.x, end.y - pointA.y);
  const midX = (sA.x + sEnd.x) / 2;
  const midY = (sA.y + sEnd.y) / 2;
  const text = `${dist.toFixed(1)} m`;
  rc.ctx.font = 'bold 12px sans-serif';
  const w = rc.ctx.measureText(text).width + 10;
  rc.ctx.fillStyle = 'rgba(17, 24, 39, 0.85)';
  rc.ctx.fillRect(midX - w / 2, midY - 20, w, 17);
  rc.ctx.fillStyle = '#fbbf24';
  rc.ctx.textAlign = 'center';
  rc.ctx.textBaseline = 'middle';
  rc.ctx.fillText(text, midX, midY - 11);
}

export function drawPreviewRacks(
  rc: RenderContext,
  previews: PreviewRackPosition[],
  rackDimensionsMap: Map<string, RackDimensions>
) {
  previews.forEach(preview => {
    const dim = rackDimensionsMap.get(preview.rackNumber) || {
      width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5,
    };

    const rotated = getRotatedDimensions(dim, preview.rotation);
    const screenX = rc.offsetX + preview.x * rc.scale;
    const screenY = rc.offsetY + preview.y * rc.scale;
    const screenWidth = rotated.width * rc.scale;
    const screenDepth = rotated.depth * rc.scale;

    rc.ctx.fillStyle = preview.isValid ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)';
    rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);

    rc.ctx.strokeStyle = preview.isValid ? '#22c55e' : '#ef4444';
    rc.ctx.lineWidth = 2;
    rc.ctx.setLineDash([5, 5]);
    rc.ctx.strokeRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.setLineDash([]);

    rc.ctx.fillStyle = preview.isValid ? '#22c55e' : '#ef4444';
    rc.ctx.font = 'bold 11px sans-serif';
    rc.ctx.textAlign = 'center';
    rc.ctx.textBaseline = 'middle';
    rc.ctx.fillText(preview.rackNumber, screenX, screenY);
  });
}

export function drawRacks(
  rc: RenderContext,
  racks: RackPosition[],
  rackDimensionsMap: Map<string, RackDimensions>,
  selectedRacks: Set<string>,
  anchorRack: string | null,
  showLabels: boolean,
  showHeatmap: boolean,
  showActivity: boolean,
  heatmapLookup: Map<string, number>,
  activityLookup: Map<string, LocationActivity[]>,
  /** Flytte-modus: rackNumber → antall kandidat-bins — grønn ramme + antall-badge */
  moveCandidateRackCounts?: Map<string, number> | null
) {
  racks.forEach(rack => {
    const dim = rackDimensionsMap.get(rack.rackNumber) || {
      width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5,
    };

    const rotated = getRotatedDimensions(dim, rack.rotation);
    const screenX = rc.offsetX + rack.x * rc.scale;
    const screenY = rc.offsetY + rack.y * rc.scale;
    const screenWidth = rotated.width * rc.scale;
    const screenDepth = rotated.depth * rc.scale;

    const isSelected = selectedRacks.has(rack.rackNumber);
    const isAnchor = anchorRack === rack.rackNumber;

    // Determine rack color — egendefinert farge fra egenskaps-panelet som grunnfarge
    let fillColor = rack.color || '#3b82f6';
    if (showHeatmap) {
      // Felles innsikt-skala (blå→gul→rød); reoler uten data rendres nøytralt grå
      const intensity = heatmapLookup.get(rack.rackNumber);
      fillColor = intensity !== undefined ? insightColor(intensity) : INSIGHT_UNKNOWN_COLOR;
    }

    const rackActivities = activityLookup.get(rack.rackNumber) || [];
    if (rackActivities.length > 0 && !showHeatmap) {
      fillColor = ACTIVITY_COLORS[rackActivities[0].activityType as ActivityType] || fillColor;
    }

    // Rack rectangle
    rc.ctx.fillStyle = fillColor;
    rc.ctx.globalAlpha = isSelected ? 1 : 0.8;
    rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.globalAlpha = 1;

    // Border
    rc.ctx.strokeStyle = '#1e3a5f';
    rc.ctx.lineWidth = 2;
    rc.ctx.strokeRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);

    // Selection border
    if (isSelected || isAnchor) {
      rc.ctx.strokeStyle = isAnchor ? '#22c55e' : '#fbbf24';
      rc.ctx.lineWidth = 3;
      rc.ctx.strokeRect(screenX - screenWidth / 2 - 2, screenY - screenDepth / 2 - 2, screenWidth + 4, screenDepth + 4);
    }

    // Label
    if (showLabels) {
      rc.ctx.fillStyle = '#ffffff';
      rc.ctx.font = 'bold 14px sans-serif';
      rc.ctx.textAlign = 'center';
      rc.ctx.textBaseline = 'middle';
      rc.ctx.fillText(rack.label || rack.rackNumber, screenX, screenY);

      rc.ctx.font = '10px sans-serif';
      rc.ctx.fillStyle = '#d1d5db';
      rc.ctx.fillText(`${dim.verticals}\u00d7${dim.shelves}`, screenX, screenY + 14);
    }

    // Activity indicator
    if (rackActivities.length > 0 && showActivity) {
      const pulseSize = 8 + Math.sin(Date.now() / 200) * 3;
      rc.ctx.beginPath();
      rc.ctx.arc(screenX + screenWidth / 2 - 10, screenY - screenDepth / 2 + 10, pulseSize, 0, Math.PI * 2);
      rc.ctx.fillStyle = ACTIVITY_COLORS[rackActivities[0].activityType as ActivityType] || '#22c55e';
      rc.ctx.fill();
    }

    // Anchor icon
    if (isAnchor) {
      rc.ctx.fillStyle = '#22c55e';
      rc.ctx.font = 'bold 16px sans-serif';
      rc.ctx.fillText('\u2693', screenX - screenWidth / 2 + 10, screenY - screenDepth / 2 + 12);
    }

    // Flytte-modus: reoler med \u22651 kandidat-bin f\u00e5r gr\u00f8nn ramme + antall-badge
    const candidateCount = moveCandidateRackCounts?.get(rack.rackNumber);
    if (candidateCount) {
      rc.ctx.strokeStyle = '#22c55e';
      rc.ctx.lineWidth = 3;
      rc.ctx.strokeRect(screenX - screenWidth / 2 - 2, screenY - screenDepth / 2 - 2, screenWidth + 4, screenDepth + 4);

      const badgeText = String(candidateCount);
      rc.ctx.font = 'bold 11px sans-serif';
      const badgeRadius = Math.max(9, rc.ctx.measureText(badgeText).width / 2 + 5);
      const badgeX = screenX + screenWidth / 2;
      const badgeY = screenY - screenDepth / 2;
      rc.ctx.beginPath();
      rc.ctx.arc(badgeX, badgeY, badgeRadius, 0, Math.PI * 2);
      rc.ctx.fillStyle = '#16a34a';
      rc.ctx.fill();
      rc.ctx.strokeStyle = '#ffffff';
      rc.ctx.lineWidth = 1.5;
      rc.ctx.stroke();
      rc.ctx.fillStyle = '#ffffff';
      rc.ctx.textAlign = 'center';
      rc.ctx.textBaseline = 'middle';
      rc.ctx.fillText(badgeText, badgeX, badgeY);
    }
  });
}

/** Oppslag binCode/rackNumber → world-punkt (meter). null = kan ikke plasseres i kartet. */
export type PendingMovePointResolver = (
  binCode: string,
  rackNumber?: string | null
) => { x: number; y: number } | null;

const PENDING_MOVE_COLOR = '#f59e0b';
const PENDING_MOVE_BADGE_COLOR = '#d97706';

/**
 * Ventende flytteordrer som oransje kurvede piler fra kilde til mål.
 * Tegnes ETTER reolene (øverst). Flytt der verken bin eller reol er plassert
 * i layouten hoppes stille over. Flere flytt mellom samme punkt-par slås
 * sammen til én pil med antall-badge midt på kurven.
 */
export function drawPendingMoves(
  rc: RenderContext,
  moves: ServerPendingMove[],
  resolvePoint: PendingMovePointResolver
) {
  // Grupper på oppløste endepunkter (bins i samme reol deler reol-senter →
  // identiske piler skal ikke tegnes oppå hverandre)
  const groups = new Map<string, { from: { x: number; y: number }; to: { x: number; y: number }; count: number }>();
  moves.forEach(move => {
    const from = resolvePoint(move.fromBinCode, move.fromRackNumber);
    const to = resolvePoint(move.toBinCode, move.toRackNumber);
    if (!from || !to) return; // uplassert endepunkt → hopp over (ikke krasj)
    const key = `${from.x.toFixed(2)},${from.y.toFixed(2)}->${to.x.toFixed(2)},${to.y.toFixed(2)}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      groups.set(key, { from, to, count: 1 });
    }
  });

  const { ctx } = rc;
  groups.forEach(({ from, to, count }) => {
    const x0 = rc.offsetX + from.x * rc.scale;
    const y0 = rc.offsetY + from.y * rc.scale;
    const x2 = rc.offsetX + to.x * rc.scale;
    const y2 = rc.offsetY + to.y * rc.scale;

    const dx = x2 - x0;
    const dy = y2 - y0;
    const len = Math.hypot(dx, dy);
    if (len < 2) return; // degenerert (samme punkt) — ingenting fornuftig å tegne

    // Kontrollpunkt: midtpunkt løftet vinkelrett ~15 % av avstanden
    const lift = len * 0.15;
    const cx = (x0 + x2) / 2 + (-dy / len) * lift;
    const cy = (y0 + y2) / 2 + (dx / len) * lift;

    ctx.save();

    // Kurvet linje (kvadratisk bezier)
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.quadraticCurveTo(cx, cy, x2, y2);
    ctx.strokeStyle = PENDING_MOVE_COLOR;
    ctx.lineWidth = 2.5;
    ctx.globalAlpha = 0.9;
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Liten sirkel ved kilden
    ctx.beginPath();
    ctx.arc(x0, y0, 4, 0, Math.PI * 2);
    ctx.fillStyle = PENDING_MOVE_COLOR;
    ctx.fill();

    // Pilhode ved målet — retning = tangenten på slutten av kurven (kontroll → mål)
    const angle = Math.atan2(y2 - cy, x2 - cx);
    const headLength = 11;
    const headWidth = Math.PI / 7;
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(
      x2 - headLength * Math.cos(angle - headWidth),
      y2 - headLength * Math.sin(angle - headWidth)
    );
    ctx.lineTo(
      x2 - headLength * Math.cos(angle + headWidth),
      y2 - headLength * Math.sin(angle + headWidth)
    );
    ctx.closePath();
    ctx.fillStyle = PENDING_MOVE_COLOR;
    ctx.fill();

    // Antall-badge midt på kurven ved flere flytt mellom samme par
    if (count > 1) {
      // Kurvepunkt ved t=0,5: B(0,5) = 0,25·P0 + 0,5·C + 0,25·P2
      const midX = 0.25 * x0 + 0.5 * cx + 0.25 * x2;
      const midY = 0.25 * y0 + 0.5 * cy + 0.25 * y2;
      const badgeText = String(count);
      ctx.font = 'bold 11px sans-serif';
      const badgeRadius = Math.max(9, ctx.measureText(badgeText).width / 2 + 5);
      ctx.beginPath();
      ctx.arc(midX, midY, badgeRadius, 0, Math.PI * 2);
      ctx.fillStyle = PENDING_MOVE_BADGE_COLOR;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(badgeText, midX, midY);
    }

    ctx.restore();
  });
}

export function drawRubberBand(rc: RenderContext, rubberBand: RubberBandState) {
  if (!rubberBand.isActive) return;

  const { startX, startY, currentX, currentY } = rubberBand;
  const x = Math.min(startX, currentX);
  const y = Math.min(startY, currentY);
  const w = Math.abs(currentX - startX);
  const h = Math.abs(currentY - startY);

  rc.ctx.strokeStyle = '#3b82f6';
  rc.ctx.lineWidth = 2;
  rc.ctx.setLineDash([5, 5]);
  rc.ctx.strokeRect(x, y, w, h);
  rc.ctx.setLineDash([]);

  rc.ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
  rc.ctx.fillRect(x, y, w, h);
}

/**
 * Canvas-overlays: kun m\u00e5lestokk-indikator.
 * Utvalgsinfo vises i statuslinjen nederst og hurtigtastene i toolbarens hjelp-popover.
 */
export function drawOverlays(rc: RenderContext, scale: number) {
  // Scale indicator
  rc.ctx.fillStyle = '#9ca3af';
  rc.ctx.font = '11px sans-serif';
  rc.ctx.textAlign = 'left';
  const scaleBarLength = 5;
  const scaleBarPixels = scaleBarLength * scale;
  rc.ctx.fillRect(20, rc.height - 30, scaleBarPixels, 4);
  rc.ctx.fillText(`${scaleBarLength}m`, 20, rc.height - 38);
}
